import React, { useState, useEffect } from 'react';
import {
  PlusCircle,
  Edit2,
  Trash2,
  X,
  Image as ImageIcon,
  Calendar,
  User,
  Upload,
  Clock,
  MessageSquare,
} from 'lucide-react';
import englishService from '../services/englishService';

// Custom Alert Component
const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>
    {children}
  </div>
);

const ProjectManagement = () => {
  const [projects, setProjects] = useState([]); // Initialize as an empty array
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const projectStatuses = ['planning', 'in-progress', 'completed', 'on-hold'];

  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    content: [''],
    AnyLink: '',
    date: new Date().toISOString().split('T')[0],
    projectManager: '',
    status: 'planning',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    images: [],
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    console.log('Projects State:', projects); // Debugging line
  }, [projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await englishService.projects.getAll(); // Corrected method call
      console.log('API Response:', response); // Debugging line
      setProjects(response.data || response); // Adjust based on API response structure
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === 'content') {
      const newContent = [...formData.content];
      newContent[index] = value;
      setFormData(prev => ({ ...prev, content: newContent }));
    } else if (name === 'AnyLink') {
      setFormData(prev => ({ ...prev, AnyLink: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddParagraph = () => {
    setFormData(prev => ({
      ...prev,
      content: [...prev.content, ''],
    }));
  };

  const handleRemoveParagraph = (index) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'thumbnail') {
          setFormData(prev => ({ ...prev, thumbnail: reader.result }));
        } else if (type === 'image') {
          if (formData.images.length >= 5) {
            setError('Maximum 5 images are allowed');
            return;
          }
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, reader.result],
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.thumbnail) {
      setError('Thumbnail is required');
      return false;
    }
    if (!formData.content[0]) {
      setError('Content is required');
      return false;
    }
    if (!formData.startDate) {
      setError('Start date is required');
      return false;
    }
    if (formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      setError('End date must be after start date');
      return false;
    }
    if (formData.images.length > 5) {
      setError('Maximum 5 images are allowed');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
  
    try {
      setLoading(true);
      setError('');
  
      const formDataToSend = {
        title: formData.title.trim(),
        thumbnail: formData.thumbnail,
        content: formData.content.filter(paragraph => paragraph.trim() !== ''),
        AnyLink: formData.AnyLink || '',
        date: new Date(formData.date).toISOString(),
        projectManager: formData.projectManager?.trim() || '',
        status: formData.status,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : '',
        images: formData.images || [],
      };
  
      let response;
      if (selectedProject) {
        response = await englishService.projects.update(selectedProject._id, formDataToSend);
      } else {
        response = await englishService.projects.create(formDataToSend);
      }
  
      console.log('API Response:', response); // Debugging line
  
      if (response.success) {
        setSuccess(selectedProject ? 'Project updated successfully!' : 'Project created successfully!');
        await fetchProjects(); // Refresh the projects list
        setShowModal(false);
        resetForm();
      } else {
        throw new Error(response.message || 'Failed to save project');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to save project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setFormData({
      title: project.title,
      content: project.content,
      AnyLink: project.AnyLink || '',
      thumbnail: project.thumbnail,
      date: new Date(project.date).toISOString().split('T')[0],
      projectManager: project.projectManager || '',
      status: project.status,
      startDate: new Date(project.startDate).toISOString().split('T')[0],
      endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
      images: project.images || [],
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await englishService.projects.delete(selectedProject._id);
      setSuccess('Project deleted successfully!');
      fetchProjects();
      setShowDeleteModal(false);
      setSelectedProject(null);
    } catch (error) {
      setError(error.message || 'Failed to delete project');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      thumbnail: '',
      content: [''],
      AnyLink: '',
      date: new Date().toISOString().split('T')[0],
      projectManager: '',
      status: 'planning',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      images: [],
    });
    setSelectedProject(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on-hold':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatusText = (status) => {
    return status.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Project Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add Project
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Projects List */}
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  {projects.map((project) => {
    console.log('Rendering Project:', project); // Debugging line
    return (
      <div key={project._id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="h-48 overflow-hidden">
          <img
            src={project.thumbnail || '/api/placeholder/400/200'}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-4">
          <div className="flex items-center mb-4">
            <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(project.status)}`}>
              {formatStatusText(project.status)}
            </span>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-4">{project.title}</h3>
          <div className="space-y-2 text-gray-600 text-sm">
            {project.projectManager && (
              <div className="flex items-center">
                <User size={16} className="mr-2" />
                {project.projectManager}
              </div>
            )}
            <div className="flex items-center">
              <Calendar size={16} className="mr-2" />
              Start: {new Date(project.startDate).toLocaleDateString()}
            </div>
            {project.endDate && (
              <div className="flex items-center">
                <Clock size={16} className="mr-2" />
                End: {new Date(project.endDate).toLocaleDateString()}
              </div>
            )}
            <div className="flex items-center">
              <MessageSquare size={16} className="mr-2" />
              {project.comments?.length || 0} comments
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <button
              onClick={() => handleEdit(project)}
              className="p-2 text-sky-500 hover:bg-sky-50 rounded-full transition-colors"
            >
              <Edit2 size={20} />
            </button>
            <button
              onClick={() => {
                setSelectedProject(project);
                setShowDeleteModal(true);
              }}
              className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  })}
</div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedProject ? 'Edit Project' : 'Add Project'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  >
                    {projectStatuses.map(status => (
                      <option key={status} value={status}>
                        {formatStatusText(status)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Project Manager */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Manager
                  </label>
                  <input
                    type="text"
                    name="projectManager"
                    value={formData.projectManager}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Upload thumbnail</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'thumbnail')}
                          required={!formData.thumbnail}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Content Paragraphs */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">
                      Content *
                    </label>
                    <button
                      type="button"
                      onClick={handleAddParagraph}
                      className="text-sky-500 hover:text-sky-600"
                    >
                      + Add Paragraph
                    </button>
                  </div>
                  {formData.content.map((paragraph, index) => (
                    <div key={index} className="relative">
                      <textarea
                        name="content"
                        value={paragraph}
                        onChange={(e) => handleInputChange(e, index)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 min-h-[100px]"
                        required
                      />
                      {formData.content.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveParagraph(index)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-600"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Any Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Any Link
                  </label>
                  <input
                    type="text"
                    name="AnyLink"
                    value={formData.AnyLink}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Additional Images */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Additional Images (Max 5)
                                  </label>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {formData.images.map((image, index) => (
                                      <div key={index} className="relative">
                                        <img
                                          src={image}
                                          alt={`Additional ${index + 1}`}
                                          className="w-full h-24 object-cover rounded-lg"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setFormData(prev => ({
                                              ...prev,
                                              images: prev.images.filter((_, i) => i !== index)
                                            }));
                                          }}
                                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                        >
                                          <X size={16} />
                                        </button>
                                      </div>
                                    ))}
                                    {formData.images.length < 5 && (
                                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                                        <Upload className="text-gray-400 mb-2" size={24} />
                                        <span className="text-sm text-gray-600">Add image</span>
                                        <input
                                          type="file"
                                          className="hidden"
                                          accept="image/*"
                                          onChange={(e) => handleFileChange(e, 'image')}
                                        />
                                      </label>
                                    )}
                                  </div>
                                </div>
                
                                {/* Submit Button */}
                                <div className="flex justify-end">
                                  <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
                                  >
                                    {loading ? 'Saving...' : (selectedProject ? 'Update Project' : 'Create Project')}
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        </div>
                      )}
                
                      {/* Delete Confirmation Modal */}
                      {showDeleteModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                          <div className="bg-white rounded-lg w-full max-w-md p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Project</h2>
                            <p className="text-gray-600 mb-6">
                              Are you sure you want to delete "{selectedProject.title}"? This action cannot be undone.
                            </p>
                            <div className="flex justify-end space-x-4">
                              <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleDelete}
                                disabled={loading}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                              >
                                {loading ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                
                      {/* Loading State */}
                      {loading && !showModal && !showDeleteModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-lg p-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                };
                
                export default ProjectManagement;